import { Capacitor } from "@capacitor/core";
import { GqlUser } from "@penfold/graphql";
import { differenceInMinutes } from "date-fns";
import { useRouter } from "next/router";
import { MutableRefObject, useEffect, useRef } from "react";
import {
  AnalyticsEvent,
  removeSensitiveUserData,
  schema,
} from "shared/analytics";
import { useAppActive } from "./app/hooks";
import { debug } from "./log";
import { LandingPageType } from "./storage/landingPageTypeStorage";

const minutesSinceDateRef = (ref: MutableRefObject<Date | null>) => {
  if (!ref.current) {
    return null;
  }
  return differenceInMinutes(new Date(), ref.current);
};

// Ignore routes like /app/launch
const useIsAccountPage = () => {
  const router = useRouter();
  return router.pathname.startsWith("/account");
};

export const useTrackSession = (loggedIn) => {
  const isAppActive = useAppActive();
  const isAccountPage = useIsAccountPage();
  const backgroundedAtRef = useRef<Date>(null);

  // Track start of session
  useEffect(() => {
    if (loggedIn && isAccountPage) {
      window.analytics.track(schema.SESSION_STARTED_UI);
    }
  }, [loggedIn, isAccountPage]);

  // Track app background time
  useEffect(() => {
    if (Capacitor.isPluginAvailable("App") && !isAppActive) {
      backgroundedAtRef.current = new Date();
    }
  }, [isAppActive]);

  // Track re-entering app after backgrounding
  useEffect(() => {
    if (
      Capacitor.isPluginAvailable("App") &&
      isAppActive &&
      loggedIn &&
      isAccountPage &&
      minutesSinceDateRef(backgroundedAtRef) >= 15
    ) {
      window.analytics.track(schema.SESSION_STARTED_UI);
      backgroundedAtRef.current = null;
    }
  }, [isAppActive, loggedIn, isAccountPage]);
};

export type UserWithAnalyticsFields = Pick<GqlUser, "id" | "intercomIdentity">;

const handleError = (err) => {
  if (err) {
    debug.error(err);
    // TODO(sdgluck): reinstate when PEN-758 is done
    // Sentry.captureException(err);
  }
};

const trackUserPurchase = () => {
  window.branch?.logEvent("PURCHASE", {}, [], undefined, handleError);

  if (Capacitor.isPluginAvailable("FirebaseAnalytics")) {
    Capacitor.Plugins.FirebaseAnalytics.logEvent({
      name: "purchase",
    }).catch(handleError);
  }

  if (Capacitor.isPluginAvailable("FacebookAnalytics")) {
    Capacitor.Plugins.FacebookAnalytics.logEvent({
      event: "fb_mobile_purchase",
    }).catch(handleError);
  }
};

const trackUserRegistration = (
  user: UserWithAnalyticsFields,
  initialLandingPageTypeVisited: LandingPageType
) => {
  window.analytics?.alias(user.id);

  analytics.identifyUser(user, { initialLandingPageTypeVisited });

  analytics.trackEvent(schema.USER_CREATED_ACCOUNT);

  window.branch?.logEvent(
    "COMPLETE_REGISTRATION",
    {},
    [],
    undefined,
    handleError
  );

  if (Capacitor.isPluginAvailable("FacebookAnalytics")) {
    Capacitor.Plugins.FacebookAnalytics.logEvent({
      event: "fb_mobile_complete_registration",
    }).catch(handleError);
  }

  if (Capacitor.isPluginAvailable("FirebaseAnalytics")) {
    Capacitor.Plugins.FirebaseAnalytics.logEvent({
      name: "sign_up",
    }).catch(handleError);
  }
};

const identifyUser = (
  user: UserWithAnalyticsFields,
  additionalSafeFields?: Record<string, any>
) => {
  if (!user) {
    handleError(new Error("identifyUser expects a user object"));
    return;
  }

  if (!user.id) {
    handleError(new Error("identifyUser expects a user object with id field"));
    return;
  }

  if (!user.intercomIdentity) {
    handleError(
      new Error(
        "identifyUser expects a user object with intercomIdentity field"
      )
    );
    return;
  }

  const identifyData = {
    ...removeSensitiveUserData(user),
    ...additionalSafeFields,
  };
  window.analytics?.identify(user.id, identifyData, {
    Intercom: {
      user_hash: user.intercomIdentity,
    },
  });

  window.branch?.setIdentity(user.id, handleError);

  if (Capacitor.isPluginAvailable("IntercomPlugin")) {
    Capacitor.Plugins.IntercomPlugin.setUserHash({
      hmac: user.intercomIdentity,
    }).catch(handleError);
    Capacitor.Plugins.IntercomPlugin.registerIdentifiedUser({
      userId: user.id,
    }).catch(handleError);
  }

  if (Capacitor.isPluginAvailable("Analytics")) {
    Capacitor.Plugins.Analytics.identify({
      userId: user.id,
    }).catch(handleError);
  }
};

const trackUserSignOut = () => {
  window.analytics?.reset();

  window.branch?.logout(handleError);

  if (Capacitor.isPluginAvailable("IntercomPlugin")) {
    Capacitor.Plugins.IntercomPlugin.logout()
      .then(() => Capacitor.Plugins.IntercomPlugin.registerUnidentifiedUser())
      // in case user came from a path with a hidden launcher
      .then(() => Capacitor.Plugins.IntercomPlugin.displayLauncher())
      .catch(handleError);
  }
};

const trackEvent = <TData = Record<string, unknown> | undefined>(
  eventName: AnalyticsEvent,
  data: TData
) => {
  window.analytics?.track(eventName, data);
};

export const analytics = {
  identifyUser,
  trackEvent,
  trackUserRegistration,
  trackUserPurchase,
  trackUserSignOut,
};
